<template>
  <button
    v-if="loading"
    :class="classList"
    tabindex="0"
    @click.prevent>
      <div class="a-button__spinner"></div>
    <slot />
  </button>
  <button
    v-else-if="subtype === 'submit'"
    :class="classList"
    tabindex="0">
      <slot />
  </button>
  <a
    v-else-if="disabled"
    :class="classList"
    tabindex="0">
    <slot />
  </a>
  <a
    v-else-if="action && !disabled && !to"
    :class="classList"
    tabindex="0"
    @click.stop="action">
      <span v-if="tooltip" class="a-button__tooltip">{{ tooltip }}</span>
    <slot />
  </a>
  <a
    v-else-if="href === '#'"
    :class="classList"
    tabindex="0"
    :href="href"
    @click.prevent>
    <slot />
  </a>
  <a
    v-else-if="href"
    :class="classList"
    tabindex="0"
    :href="href"
    :target="target">
      <span v-if="tooltip" class="a-button__tooltip">{{ tooltip }}</span>
    <slot />
  </a>
  <router-link
    v-else-if="to && !action"
    :class="classList"
    tabindex="0"
    :to="to">
      <slot />
  </router-link>
  <router-link
    v-else-if="to && action"
    :class="classList"
    tabindex="0"
    :to="to"
    @click.native="action">
      <slot />
  </router-link>
</template>

<script>
export default {
  name: 'v-button',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    transition: {
      type: Boolean,
      default: false,
    },
    fullwidth: {
      type: Boolean,
      default: false,
    },
    type: [String],
    size: [String],
    href: [String],
    target: [String],
    action: [Function],
    to: [String],
    subtype: [String],
    loading: [Boolean],
    tooltip: [String],
  },
  computed: {
    classList() {
      const {
        type, size, disabled, loading, iconOnly, transition, fullwidth,
      } = this;
      return {
        'a-button': true,
        'a-button--disabled': disabled,
        'a-button--loading': loading,
        'a-button--icon-only': iconOnly,
        'a-button--transition': transition,
        'a-button--full-width': fullwidth,
        [`a-button--${type}`]: type,
        [`a-button--${size}`]: size,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.a-button';

  $color-gradient-default: $signable-secondary-4-shade-3;
  $color-gradient-default-lighter: lighten($color-gradient-default, 5%);
  $color-gradient-primary-1: $signable-primary-1;
  $color-gradient-primary-1-lighter: lighten($color-gradient-primary-1, 5%);
  $color-gradient-primary-2: $signable-primary-2;
  $color-gradient-primary-2-lighter: lighten($color-gradient-primary-2, 15%);
  $color-gradient-primary-3: $signable-secondary-1;
  $color-gradient-primary-3-lighter: lighten($color-gradient-primary-3, 10%);

  #{$parent} {
    @include verticalGradientBackground(
      $color-gradient-default,
      $color-gradient-default-lighter
    );
    border: 1px solid $color-border;
    padding: $spacing-small $spacing-base;
    float: left;
    border-radius: 3px;
    cursor: pointer;
    font-weight: 600;
    line-height: 1;
    text-decoration: none;
    color: inherit;
    font-size: inherit;
    font-family: inherit;
    position: relative;
    &:hover {
      @include verticalGradientBackground(
        $color-gradient-default-lighter,
        $color-gradient-default
      );
      color: $signable-primary-1;
      border-color: $signable-primary-1;
    }
    &:active {
      background: $color-gradient-default;
      color: $signable-primary-1-shade-1;
      border-color: $signable-primary-1-shade-1;
      outline: none; // TODO Accessibiliy Issue
    }
    &:focus {
      outline: none; // TODO Accessibiliy Issue
    }
    /deep/ svg {
      margin-right: $spacing-small;
      float: left;
      height: 14px;
      width: 14px;
      pointer-events: none;
    }
    &__spinner {
      border-radius: 50%;
      border: 2px solid $signable-primary-1-shade-1;
      border-top-color: $signable-primary-1-shade-2;
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.4), inset 0 0 5px 0 rgba(0, 0, 0, 0.4);
      height: 26px;
      width: 26px;
      top: 50%;
      left: 50%;
      position: relative;
      animation: spinner 1.5s linear infinite;
      transition: 1s ease;
      z-index: 2;
      @include alignElement(true, true);
    }
    // modifiers
    &--cta-1 {
      @include verticalGradientBackground(
        $color-gradient-primary-1,
        $color-gradient-primary-1-lighter
      );
      border: 1px solid $signable-primary-1;
      color: #fff;
      &:hover {
        @include verticalGradientBackground(
          $color-gradient-primary-1-lighter,
          $color-gradient-primary-1
        );
        color: #fff;
        border: 1px solid $signable-primary-1;
      }
      &:active {
        background: $signable-primary-1;
        color: #fff;
        border: 1px solid $signable-primary-1;
      }
    }
    &--cta-2 {
      @include verticalGradientBackground(
        $color-gradient-primary-2,
        $color-gradient-primary-2-lighter
      );
      border-color: $signable-primary-2;
      color: #fff;
      &:hover {
        @include verticalGradientBackground(
          $color-gradient-primary-2-lighter,
          $color-gradient-primary-2
        );
        color: #fff;
        border: 1px solid $signable-primary-2;
      }
      &:active {
        background: $signable-primary-2;
        color: #fff;
        border: 1px solid $signable-primary-2;
      }
    }
    &--cta-3 {
      @include verticalGradientBackground(
        $color-gradient-primary-3,
        $color-gradient-primary-3-lighter
      );
      border-color: $signable-secondary-1;
      color: #fff;
      &:hover {
        @include verticalGradientBackground(
          $color-gradient-primary-3-lighter,
          $color-gradient-primary-3
        );
        color: #fff;
        border: 1px solid $signable-secondary-1;
      }
      &:active {
        background: $signable-secondary-1;
        color: #fff;
        border: 1px solid $signable-secondary-1;
      }
    }
    &--outline-1 {
      border: 1px solid $signable-primary-1;
      color: $signable-primary-1;
      &:hover {
        color: #fff;
        background: $signable-primary-1;
      }
      &:active {
        border-color: $signable-primary-1;
        background: $color-gradient-primary-1-lighter;
        color: #fff;
      }
    }
    &--outline-2 {
      border: 1px solid $signable-primary-2;
      color: darken($signable-primary-2, 5%);
      &:hover {
        color: #fff;
        background: $signable-primary-2;
        border-color: $signable-primary-2;
      }
      &:active {
        border-color: $signable-primary-2;
        background: $signable-primary-2-shade-1;
        color: #fff;
      }
    }
    &--outline-3 {
      border: 1px solid $signable-secondary-1;
      color: $signable-secondary-1;
      &:hover {
        color: #fff;
        background: $signable-secondary-1;
        border-color: $signable-secondary-1;
      }
      &:active {
        border-color: $signable-secondary-1;
        background: $color-gradient-primary-3-lighter;
        color: #fff;
      }
    }
    &--outline-4 {
      border: 1px solid $signable-secondary-2;
      color: $signable-secondary-2;
      &:hover {
        color: #fff;
        background: $signable-secondary-2;
        border-color: $signable-secondary-2;
      }
      &:active {
        border-color: $signable-secondary-2;
        background: $color-gradient-primary-3-lighter;
        color: #fff;
      }
    }
    &--outline-success {
      border-color: $color-success;
      color: $color-success;
      &:hover {
        border-color: $color-success-dark;
        background: $color-success;
        color: #fff;
      }
    }
    &--outline-warning {
      border-color: $color-warning;
      color: $color-warning;
      &:hover {
        border-color: $color-warning-dark;
        background: $color-warning;
        color: #fff;
      }
    }
    &--outline-error {
      border-color: $color-error;
      color: $color-error;
      &:hover {
        border-color: $color-error-dark;
        background: $color-error;
        color: #fff;
      }
    }
    &--success {
      background: $color-success;
      color: #fff;
      border: 1px solid $color-success-dark;
      &:hover,
      &:active {
        background: $color-success-dark;
        border-color: $color-success-dark;
        color: #fff;
      }
    }
    &--warning {
      background: $color-warning;
      color: #fff;
      border: 1px solid $color-warning-dark;
      &:hover,
      &:active {
        background: $color-warning-dark;
        border-color: $color-warning-dark;
        color: #fff;
      }
    }
    &--error {
      background: $color-error;
      color: #fff;
      border: 1px solid $color-error-dark;
      &:hover,
      &:active,
      &:focus {
        background: $color-error-dark;
        border-color: $color-error-dark;
        color: #fff;
      }
    }
    &--large {
      font-size: 20px;
      /deep/ svg {
        height: 20px;
        width: 16px;
      }
    }
    &--small {
      font-size: $font-size-small;
      padding: $spacing-xs $spacing-small;
      svg {
        margin-right: $spacing-xs;
      }
    }
    &--input {
      height: 45px;
      padding: $spacing-base $spacing-base;
    }
    &--icon-only {
      padding: $spacing-small;
      border-radius: 30px;
      position: relative;
      &:hover {
        #{$parent}__tooltip {
          display: block;
        }
      }
      svg {
        margin-right: 0;
        height: 16px;
        width: 16px;
      }
      #{$parent}__tooltip {
        position: absolute;
        display: none;
        font-size: 11px;
        top: 0;
        width: 100%;
        width: max-content;
        background: #333;
        padding: 5px 7px;
        color: #fff;
        border-radius: 30px;
        @include alignElement(true, false);
        top: -25px;
      }
      &#{$parent}--small {
        padding: $spacing-xs + 2;
        svg {
          margin-right: 0;
          height: 12px;
          width: 12px;
        }
      }
    }
    &--disabled, &--loading {
      background: $signable-secondary-4-shade-3;
      border: 1px solid $signable-secondary-4-shade-1;
      color: $signable-secondary-4;
      &:hover,
      &:active {
        cursor: not-allowed;
        background: $signable-secondary-4-shade-3;
        border: 1px solid $signable-secondary-4-shade-2;
        color: $signable-secondary-4-shade-1;
      }
    }
    &--transition {
      transition: 0.25s all ease;
    }
    &--full-width {
      text-align: center;
      width: 100%;
      svg {
        float: none;
        vertical-align: bottom;
      }
    }
  }

  @keyframes spinner {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
</style>
